import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Crown, Skull, Star, ChevronLeft, ChevronRight, X as XIcon, Sparkles, Flame, Calendar } from 'lucide-react';
import { useAppMode } from '../context/DemoContext';

// ─────────────────────────────────────────────────────────────────────────────
// Mock immortalised ghosts. Replaced later by an Anchor program read of all
// ImmortalGhost PDAs. Program ID: C5vRjZZAjG1uzcir9y2tw6dwMjH79fPwQgoKaSjEnACT
// ─────────────────────────────────────────────────────────────────────────────
const MOCK_GHOSTS = [
  { season: 3, rank: 1, wallet: '7xBQjf3vJ9Ri8XkSAdmZWJ8KKoBrYpjFeLcRkLW2NhJN', sp: 184_320, tokensBurned: 5_120_000, reputation: 962, immortalisedAt: '2026-04-30', title: 'The Ash Sovereign' },
  { season: 3, rank: 2, wallet: 'C2kQkiowaVKZDXeFd7LE84WZJowrpgYA7DrhPpxqY6S4', sp: 168_905, tokensBurned: 4_280_000, reputation: 887, immortalisedAt: '2026-04-30', title: 'Warden of the Pyre' },
  { season: 3, rank: 3, wallet: 'D9pNvQ8mYfEkRdLs3VhT6oPjQ2cGzBH4FmKn7AaR1tWv', sp: 142_780, tokensBurned: 3_560_000, reputation: 812, immortalisedAt: '2026-04-30', title: 'Iron-Eyed Pilgrim' },
  { season: 3, rank: 4, wallet: '4tHjK9LpQrTzM2vN8XwYf7Jb3CcDsEgF6AaGgB1UuV5N', sp: 121_004, tokensBurned: 2_840_000, reputation: 744, immortalisedAt: '2026-04-30', title: 'Sewer Cardinal' },
  { season: 2, rank: 1, wallet: 'EpZN3qXvL8m2KdH9YwR4PsTjC5nB7AaG6FfDdEeS1uV2', sp: 156_402, tokensBurned: 4_540_000, reputation: 905, immortalisedAt: '2026-03-30', title: 'The Cinder Apostle' },
  { season: 2, rank: 2, wallet: 'F2K8mJ4xLpR9NvW6YbZcDsEgH3tJq5UuX7AaB1CvT8eN', sp: 138_991, tokensBurned: 3_820_000, reputation: 851, immortalisedAt: '2026-03-30', title: 'Silent Bookkeeper' },
  { season: 2, rank: 3, wallet: 'G6N9pQrTzM2vN8XwYf7Jb3CcDsEgF6AaGgB1UuV5K4tH', sp: 124_318, tokensBurned: 3_140_000, reputation: 783, immortalisedAt: '2026-03-30', title: 'Marrow-Knight' },
  { season: 2, rank: 4, wallet: 'H8mYfEkRdLs3VhT6oPjQ2cGzBH4FmKn7AaR1tWvD9pNv', sp: 109_607, tokensBurned: 2_520_000, reputation: 712, immortalisedAt: '2026-03-30', title: 'Choir of the Drain' },
  { season: 1, rank: 1, wallet: 'JaP7uQ4vM3kNrS2L9oXcYbZdEfGhJ5K6T8B1Au0WV9eS', sp: 132_550, tokensBurned: 3_960_000, reputation: 878, immortalisedAt: '2026-02-28', title: 'First of the Eternal' },
  { season: 1, rank: 2, wallet: 'KeQ8vR5wM4lOsT3M0pYdZcAfGiJk6L7U9C2Bv1WV0fT', sp: 118_804, tokensBurned: 3_210_000, reputation: 819, immortalisedAt: '2026-02-28', title: 'Lantern-Bearer' },
  { season: 1, rank: 3, wallet: 'L5tPmJ4xLpR9NvW6YbZcDsEgH3tJq5UuX7AaB1CvT8eN', sp: 106_113, tokensBurned: 2_740_000, reputation: 768, immortalisedAt: '2026-02-28', title: 'The Soft-Spoken' },
  { season: 1, rank: 4, wallet: 'M2J9rOzWqXcVbN5kPlH4FdGfHjKkL6T7B8A1Cz0V9eU', sp: 94_551, tokensBurned: 2_180_000, reputation: 694, immortalisedAt: '2026-02-28', title: 'Coin-Eyed Nameless' },
];

const SEASONS = [3, 2, 1];
const truncate = (s) => `${s.slice(0, 4)}…${s.slice(-4)}`;

const RANK_META = {
  1: { Icon: Crown, color: '#ffd86b', crimson: '#c8413c', label: 'SOVEREIGN', hue: '255,180,80', crimsonHue: '200,65,60' },
  2: { Icon: Star, color: '#d89060', crimson: '#c8413c', label: 'WARDEN', hue: '216,144,96', crimsonHue: '200,65,60' },
  3: { Icon: Skull, color: '#c87060', crimson: '#a83838', label: 'PILGRIM', hue: '200,112,96', crimsonHue: '168,56,56' },
  4: { Icon: Skull, color: '#a85050', crimson: '#8b2828', label: 'CARDINAL', hue: '168,80,80', crimsonHue: '139,40,40' },
};

const GREMLIN_IMG_BY_RANK = {
  1: 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/aqi0bkiv_polyyyyyyyy_Vertical_full_body_composition_of_a_small_hunched_bc5de0ac-1b49-4c6b-8ed1-03c06e81ee1d_1%20%281%29.webp',
  2: 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/hln6mw84_polyyyyyyyy_Vertical_full_body_composition_of_a_frail_ancient_f25924ad-ffb6-43f2-90a0-ffd8e06cf982_0%20%281%29.webp',
  3: 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/vo2x7tu7_polyyyyyyyy_Vertical_full_body_composition_of_a_thin_wiry_gre_e74a018b-1372-45f2-b998-0008207e03b1_1%20%281%29.webp',
  4: 'https://customer-assets.emergentagent.com/job_e8127900-2fd6-4ff4-a7bb-1f029eaa8bfc/artifacts/lgjym59t_polyyyyyyyy_Vertical_full_body_composition_of_a_small_twisted_45a88420-9677-41a0-83af-52bae3237352_2%20%281%29.webp',
};

// ─────────────────────────────────────────────────────────────────────────────
// Necropolis Plinth — single ghost on a carved stone altar with stats inscribed
// ─────────────────────────────────────────────────────────────────────────────
const Plinth = ({ ghost, onSelect }) => {
  const meta = RANK_META[ghost.rank];
  const isSovereign = ghost.rank === 1;

  return (
    <motion.button
      type="button"
      onClick={() => onSelect(ghost)}
      data-testid={`plinth-s${ghost.season}-r${ghost.rank}`}
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      whileHover={{ y: -8 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="relative group focus:outline-none flex flex-col"
    >
      {/* ─── FIGURE STAGE ─── */}
      <div className="relative flex-1" style={{ height: 320, minHeight: 320 }}>
        {/* Crimson aura behind figure — gives the wraith a heroic glow */}
        <div className="absolute pointer-events-none transition-opacity duration-500" style={{
          inset: '8% 8% 18% 8%',
          background: `radial-gradient(ellipse at 50% 50%, rgba(${meta.crimsonHue},0.55) 0%, rgba(${meta.crimsonHue},0.18) 35%, transparent 75%)`,
          filter: 'blur(20px)',
        }} />

        {/* Distant rune sigil disc behind sovereigns */}
        {isSovereign && (
          <div className="absolute pointer-events-none rounded-full" style={{
            top: '12%', left: '50%', transform: 'translateX(-50%)',
            width: 220, height: 220,
            border: '1px solid rgba(200,65,60,0.4)',
            boxShadow: 'inset 0 0 24px rgba(200,65,60,0.3), 0 0 30px rgba(255,180,80,0.18)',
            animation: 'plinthRingSpin 22s linear infinite',
          }}>
            {[...Array(12)].map((_, i) => (
              <div key={i} className="absolute" style={{
                left: '50%', top: '-2px',
                width: i % 3 === 0 ? '2px' : '1px',
                height: i % 3 === 0 ? '10px' : '5px',
                transformOrigin: '50% calc(50% + 1px)',
                transform: `translateX(-50%) rotate(${i * 30}deg)`,
                background: 'rgba(255,180,80,0.55)',
                boxShadow: '0 0 4px rgba(200,65,60,0.5)',
              }} />
            ))}
          </div>
        )}

        {/* The wraith figure */}
        <img
          src={GREMLIN_IMG_BY_RANK[ghost.rank]}
          alt={ghost.title}
          draggable={false}
          loading="lazy"
          className="relative w-full h-full pointer-events-none select-none transition-all duration-500"
          style={{
            objectFit: 'contain',
            mixBlendMode: 'screen',
            filter: `
              drop-shadow(0 0 22px rgba(${meta.crimsonHue},0.7))
              drop-shadow(0 0 12px rgba(${meta.hue},0.5))
              drop-shadow(0 10px 14px rgba(0,0,0,0.95))
            `,
          }}
        />

        {/* Sovereign crown crest */}
        {isSovereign && (
          <div className="absolute pointer-events-none" style={{
            top: '-2%', left: '50%',
            transform: 'translateX(-50%)',
            fontSize: 22,
            color: '#ffd86b',
            filter: 'drop-shadow(0 0 10px #ffd86b) drop-shadow(0 0 6px #c8413c)',
          }}>♛</div>
        )}
      </div>

      {/* ─── CARVED STONE PLINTH BASE ─── */}
      <div className="relative px-4 pt-3 pb-3" style={{
        background: `
          linear-gradient(180deg, rgba(34,22,10,0.99) 0%, rgba(20,12,6,1) 60%, rgba(10,6,3,1) 100%)
        `,
        borderTop: `1px solid ${meta.color}55`,
        borderLeft: '1px solid rgba(140,100,40,0.35)',
        borderRight: '1px solid rgba(140,100,40,0.35)',
        borderBottom: '1px solid rgba(180,140,55,0.45)',
        boxShadow: `
          inset 0 1px 1px rgba(255,210,120,0.18),
          inset 0 -2px 4px rgba(0,0,0,0.7),
          0 4px 12px rgba(0,0,0,0.7),
          0 0 20px rgba(${meta.crimsonHue},0.18)
        `,
      }}>
        {/* Crimson ember stripe at top of plinth */}
        <div className="absolute -top-px left-0 right-0 h-px pointer-events-none" style={{
          background: `linear-gradient(90deg, transparent 0%, rgba(${meta.crimsonHue},0.95) 50%, transparent 100%)`,
          boxShadow: `0 0 8px rgba(${meta.crimsonHue},0.7)`,
        }} />

        {/* Brass corner studs — top-left, top-right */}
        {[
          { top: 4, left: 4 }, { top: 4, right: 4 },
          { bottom: 4, left: 4 }, { bottom: 4, right: 4 },
        ].map((p, i) => (
          <div key={i} className="absolute pointer-events-none" style={{
            ...p,
            width: 4, height: 4,
            borderRadius: '50%',
            background: 'radial-gradient(circle at 35% 30%, rgba(220,180,90,0.85) 0%, rgba(110,75,25,0.95) 50%, rgba(40,25,8,1) 100%)',
            boxShadow: 'inset 0 0.5px 0.5px rgba(255,200,100,0.4), 0 0.5px 1px rgba(0,0,0,0.6)',
          }} />
        ))}

        {/* Rank label + season tag */}
        <div className="flex items-center justify-between mb-1.5">
          <div className="flex items-center gap-1.5">
            <meta.Icon className="w-3 h-3 flex-shrink-0" style={{
              color: meta.color, filter: `drop-shadow(0 0 5px ${meta.color})`,
            }} />
            <span className="text-[8px] font-bold tracking-[0.3em]" style={{
              color: meta.color, fontFamily: "'Cinzel', serif",
              textShadow: `0 0 5px ${meta.color}66, 0 1px 1px rgba(0,0,0,0.95)`,
            }}>
              {meta.label} · #{ghost.rank}
            </span>
          </div>
          <span className="text-[8px] font-bold tracking-[0.25em]" style={{
            color: '#9a7a3a', fontFamily: "'Courier New', monospace",
          }}>
            S{ghost.season}
          </span>
        </div>

        {/* Engraved title — gold gradient with crimson ghost shadow */}
        <p className="text-[15px] font-bold leading-tight mb-2 truncate" style={{
          fontFamily: "'Cinzel', serif",
          color: '#fff5dc',
          textShadow: `0 0 10px ${meta.color}aa, 0 0 16px rgba(${meta.crimsonHue},0.55), 0 1px 2px rgba(0,0,0,1)`,
          letterSpacing: '0.01em',
        }}>
          {ghost.title}
        </p>

        {/* Carved-stone divider */}
        <div className="h-px mb-2" style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(180,140,55,0.5) 30%, rgba(180,140,55,0.5) 70%, transparent 100%)',
        }} />

        {/* SP + wallet engraving */}
        <div className="flex items-end justify-between gap-2">
          <div>
            <p className="text-[8px] tracking-[0.25em] uppercase font-bold leading-none mb-0.5" style={{
              color: '#ffc870', fontFamily: "'Courier New', monospace",
            }}>
              FINAL SP
            </p>
            <p className="text-2xl font-black tracking-wide leading-none" style={{
              fontFamily: "'Impact', 'Arial Black', sans-serif",
              color: '#fff5dc',
              textShadow: `0 0 10px ${meta.color}, 0 0 18px rgba(${meta.crimsonHue},0.6), 0 1px 2px rgba(0,0,0,1)`,
            }}>
              {(ghost.sp / 1000).toFixed(1)}<span className="text-sm" style={{ color: meta.color, opacity: 0.85 }}>k</span>
            </p>
          </div>
          <p className="text-[9px] font-mono text-right leading-tight" style={{ color: '#7a6648' }}>
            {truncate(ghost.wallet)}<br/>
            <span className="text-[8px]" style={{ color: '#5a4828' }}>{ghost.immortalisedAt}</span>
          </p>
        </div>

        {/* Hover SUMMON hint */}
        <div className="absolute inset-x-0 -bottom-7 text-center pointer-events-none opacity-0 group-hover:opacity-100 group-focus:opacity-100 transition-opacity duration-300">
          <span className="text-[8px] tracking-[0.4em] font-bold" style={{
            color: meta.color, fontFamily: "'Cinzel', serif",
            textShadow: `0 0 8px ${meta.color}, 0 1px 2px rgba(0,0,0,1)`,
          }}>
            ◆ SUMMON ◆
          </span>
        </div>
      </div>

      {/* Ember pool beneath the plinth — light leaks from below */}
      <div className="absolute pointer-events-none" style={{
        left: '15%', right: '15%', bottom: '-14px',
        height: 24,
        background: `radial-gradient(ellipse at 50% 0%, rgba(${meta.crimsonHue},0.75) 0%, rgba(${meta.crimsonHue},0.25) 40%, transparent 75%)`,
        filter: 'blur(6px)',
      }} />
    </motion.button>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Preview modal — opens when a plinth is clicked
// ─────────────────────────────────────────────────────────────────────────────
const GhostPreviewModal = ({ ghost, onClose }) => {
  const navigate = useNavigate();
  const meta = RANK_META[ghost.rank];

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[300] flex items-center justify-center px-5"
      onClick={onClose}
      data-testid="ghost-preview-modal"
      style={{ background: 'rgba(2,1,0,0.88)', backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
    >
      <motion.div
        initial={{ scale: 0.92, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.92, y: 20 }}
        transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-md"
        style={{
          background: 'linear-gradient(180deg, rgba(28,18,6,0.99) 0%, rgba(12,7,2,1) 100%)',
          border: `1px solid ${meta.color}a0`,
          boxShadow: `
            inset 0 1px 1px rgba(255,210,120,0.25),
            inset 0 -1px 1px rgba(0,0,0,0.7),
            0 0 0 2px rgba(8,4,2,1),
            0 0 0 3px rgba(${meta.crimsonHue},0.55),
            0 0 50px rgba(${meta.crimsonHue},0.4),
            0 8px 24px rgba(0,0,0,0.8)
          `,
        }}
      >
        <button
          onClick={onClose}
          data-testid="ghost-preview-close"
          className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center"
          style={{ background: 'rgba(8,4,2,0.8)', border: '1px solid rgba(140,100,40,0.4)', color: '#a08858' }}
          aria-label="Close"
        >
          <XIcon className="w-4 h-4" />
        </button>

        <div className="px-6 pt-6 pb-4 flex items-start gap-4" style={{ borderBottom: '1px solid rgba(140,100,40,0.25)' }}>
          {/* Modal sigil = the actual gremlin face thumbnail (premium touch) */}
          <div className="relative flex-shrink-0 w-16 h-16 rounded-full overflow-hidden" style={{
            background: 'radial-gradient(circle at 50% 40%, rgba(0,0,0,1) 0%, rgba(0,0,0,1) 100%)',
            border: `1.5px solid ${meta.color}b0`,
            boxShadow: `inset 0 0 14px rgba(${meta.crimsonHue},0.4), 0 0 14px rgba(${meta.crimsonHue},0.55)`,
          }}>
            <img
              src={GREMLIN_IMG_BY_RANK[ghost.rank]}
              alt={ghost.title}
              draggable={false}
              className="w-full h-full"
              style={{ objectFit: 'cover', mixBlendMode: 'screen', objectPosition: '50% 28%' }}
            />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[9px] tracking-[0.3em] font-bold mb-1" style={{
              color: meta.color, fontFamily: "'Cinzel', serif",
              textShadow: `0 0 6px ${meta.color}88`,
            }}>
              {meta.label} · SEASON {ghost.season} · #{ghost.rank}
            </p>
            <h2 className="text-2xl font-black leading-tight mb-1" style={{
              fontFamily: "'Cinzel', serif",
              color: '#fff5dc',
              textShadow: `0 0 12px ${meta.color}aa, 0 1px 2px rgba(0,0,0,1)`,
            }}>
              {ghost.title}
            </h2>
            <p className="text-[11px] font-mono" style={{ color: '#a08858' }}>{truncate(ghost.wallet)}</p>
          </div>
        </div>

        <div className="px-6 py-5 grid grid-cols-2 gap-3">
          <PreviewStat Icon={Sparkles} label="FINAL SP" value={ghost.sp.toLocaleString('de-DE')} accent="#ffd86b" />
          <PreviewStat Icon={Flame} label="$GFG BURNED" value={`${(ghost.tokensBurned / 1_000_000).toFixed(2)}M`} accent="#ff8c00" />
          <PreviewStat Icon={Star} label="REPUTATION" value={ghost.reputation} accent="#88e878" />
          <PreviewStat Icon={Calendar} label="IMMORTALISED" value={ghost.immortalisedAt} accent="#a892ff" small />
        </div>

        <div className="px-6 pb-4">
          <p className="text-[12px] italic leading-relaxed text-center" style={{
            color: '#bba377', fontFamily: "'Cinzel', serif",
          }}>
            {ghost.rank === 1 ? '"Bound to the season they ruled."' : '"Their flame was seen. The Codex remembers."'}
          </p>
        </div>

        <button
          onClick={() => navigate(`/soul/${ghost.wallet}`)}
          data-testid="ghost-preview-view-full"
          className="w-full px-6 py-4 text-[11px] font-bold tracking-[0.3em] flex items-center justify-center gap-2 transition-all hover:brightness-110"
          style={{
            fontFamily: "'Cinzel', serif",
            background: 'linear-gradient(180deg, rgba(40,24,8,0.98) 0%, rgba(20,12,4,1) 100%)',
            borderTop: `1px solid ${meta.color}60`,
            color: meta.color,
            textShadow: `0 0 8px ${meta.color}88, 0 1px 2px rgba(0,0,0,1)`,
          }}
        >
          VIEW FULL SOUL
          <ChevronRight className="w-4 h-4" />
        </button>
      </motion.div>
    </motion.div>
  );
};

const PreviewStat = ({ Icon, label, value, accent, small }) => (
  <div className="px-3 py-3" style={{ background: 'rgba(8,4,2,0.7)', border: '1px solid rgba(140,100,40,0.3)' }}>
    <div className="flex items-center gap-1.5 mb-1.5">
      <Icon className="w-3 h-3" style={{ color: accent }} />
      <p className="text-[8px] font-bold tracking-[0.25em]" style={{ color: '#ffc870', fontFamily: "'Courier New', monospace" }}>{label}</p>
    </div>
    <p className={`${small ? 'text-base' : 'text-xl'} font-black tracking-wide`} style={{
      fontFamily: small ? "'Courier New', monospace" : "'Impact', sans-serif",
      color: '#fff5dc',
      textShadow: `0 0 10px ${accent}, 0 1px 2px rgba(0,0,0,1)`,
    }}>{value}</p>
  </div>
);

const FilterPill = ({ active, onClick, testId, children }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className="px-4 py-2 text-[10px] tracking-[0.25em] font-bold transition-all"
    style={{
      fontFamily: "'Cinzel', serif",
      background: active
        ? 'linear-gradient(180deg, rgba(60,20,16,0.98) 0%, rgba(24,10,8,1) 100%)'
        : 'rgba(8,4,2,0.85)',
      border: `1px solid ${active ? 'rgba(200,65,60,0.85)' : 'rgba(140,100,40,0.3)'}`,
      color: active ? '#ffd86b' : '#a08858',
      textShadow: active ? '0 0 8px rgba(255,180,80,0.6), 0 0 4px rgba(200,65,60,0.4)' : 'none',
      boxShadow: active ? '0 0 14px rgba(200,65,60,0.35), inset 0 1px 1px rgba(255,180,80,0.15)' : 'none',
      borderRadius: 1,
    }}
  >
    {children}
  </button>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN — The Necropolis
// ─────────────────────────────────────────────────────────────────────────────
export default function GhostHallPage() {
  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const navigate = useNavigate();
  const { isLive } = useAppMode();

  // Live mode (devnet) → on-chain Ghost PDAs aren't indexed yet, so the
  // hall is empty until wallets call `immortalize_ghost`. Demo / no-mode
  // → show the curated mocks so visitors get the visual story.
  const sourceGhosts = isLive ? [] : MOCK_GHOSTS;

  const filteredGhosts = useMemo(() => {
    if (filter === 'all') return sourceGhosts;
    return sourceGhosts.filter((g) => g.season === filter);
  }, [filter, sourceGhosts]);

  const handleBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate('/');
  };

  return (
    <div className="min-h-screen text-white relative overflow-hidden" style={{ background: '#020100' }}>
      <style>{`
        @keyframes plinthRingSpin { 0% { transform: translateX(-50%) rotate(0deg); } 100% { transform: translateX(-50%) rotate(360deg); } }
        @keyframes shaftSlow { 0%,100% { opacity: 0.55; transform: translateY(0); } 50% { opacity: 0.9; transform: translateY(-6px); } }
        @keyframes fogCreep { 0%,100% { transform: translateX(0); opacity: 0.7; } 50% { transform: translateX(8vw); opacity: 1; } }
      `}</style>

      {/* ════════ CATHEDRAL ENVIRONMENT ════════ */}
      {/* Stone tile bedrock */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: `
          linear-gradient(0deg, rgba(120,85,30,0.025) 1px, transparent 1px),
          linear-gradient(90deg, rgba(120,85,30,0.025) 1px, transparent 1px),
          radial-gradient(ellipse at 50% 50%, rgba(40,28,12,0.55) 0%, transparent 70%)
        `,
        backgroundSize: '120px 120px, 120px 120px, 100% 100%',
      }} />

      {/* Crimson light shafts piercing down from the ceiling — plural verticals */}
      <div className="fixed inset-0 pointer-events-none">
        {[15, 35, 50, 65, 85].map((leftPct, i) => (
          <div key={i} className="absolute top-0 bottom-0" style={{
            left: `${leftPct}%`,
            width: i % 2 === 0 ? 220 : 140,
            transform: 'translateX(-50%)',
            background: `linear-gradient(180deg, rgba(${i % 2 === 0 ? '200,65,60' : '255,140,40'},0.16) 0%, rgba(${i % 2 === 0 ? '160,40,30' : '180,90,30'},0.06) 50%, transparent 100%)`,
            filter: 'blur(40px)',
            animation: `shaftSlow ${10 + i * 2}s ease-in-out ${i * -1.5}s infinite`,
          }} />
        ))}
      </div>

      {/* Distant cathedral pillars suggested via dark column gradients on edges */}
      <div className="fixed top-0 bottom-0 left-0 pointer-events-none" style={{
        width: '12%',
        background: 'linear-gradient(90deg, rgba(0,0,0,0.85) 0%, rgba(20,12,8,0.55) 50%, transparent 100%)',
      }} />
      <div className="fixed top-0 bottom-0 right-0 pointer-events-none" style={{
        width: '12%',
        background: 'linear-gradient(270deg, rgba(0,0,0,0.85) 0%, rgba(20,12,8,0.55) 50%, transparent 100%)',
      }} />

      {/* Ground fog drifting */}
      <div className="fixed inset-x-0 bottom-0 pointer-events-none" style={{
        height: '40vh',
        background: 'radial-gradient(ellipse at 50% 100%, rgba(120,40,30,0.22) 0%, rgba(60,20,15,0.12) 35%, transparent 70%)',
        animation: 'fogCreep 22s ease-in-out infinite',
      }} />
      <div className="fixed inset-x-0 bottom-0 pointer-events-none" style={{
        height: '20vh',
        background: 'radial-gradient(ellipse at 70% 100%, rgba(40,20,10,0.5) 0%, transparent 60%)',
        animation: 'fogCreep 18s ease-in-out 4s infinite reverse',
      }} />

      {/* Top vault darkness */}
      <div className="fixed top-0 inset-x-0 pointer-events-none" style={{
        height: '30vh',
        background: 'linear-gradient(180deg, rgba(0,0,0,0.85) 0%, transparent 100%)',
      }} />

      {/* ════════ CONTENT ════════ */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-10 pb-32">
        <button onClick={handleBack} className="inline-flex items-center gap-1.5 text-[11px] tracking-[0.25em] mb-8 group" style={{
          color: '#a08858', fontFamily: "'Courier New', monospace", background: 'transparent', border: 'none',
        }} data-testid="ghost-hall-back">
          <ChevronLeft className="w-3.5 h-3.5 transition-transform group-hover:-translate-x-0.5" />
          RETURN
        </button>

        {/* HERO TITLE */}
        <motion.div
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-3"
        >
          <p className="text-[10px] tracking-[0.5em] mb-4 flex items-center justify-center gap-3" style={{
            color: '#9a7a3a', fontFamily: "'Cinzel', serif",
          }}>
            <span style={{ color: '#c8413c', textShadow: '0 0 8px rgba(200,65,60,0.7)' }}>◆</span>
            THE NECROPOLIS
            <span style={{ color: '#c8413c', textShadow: '0 0 8px rgba(200,65,60,0.7)' }}>◆</span>
          </p>
          <h1 className="text-5xl sm:text-7xl lg:text-8xl font-black tracking-tight" style={{
            fontFamily: "'Cinzel', serif",
            background: 'linear-gradient(180deg, #fff5dc 0%, #f0d68a 30%, #c69544 60%, #6e4814 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 0 40px rgba(255,180,80,0.4)) drop-shadow(0 0 50px rgba(200,65,60,0.35)) drop-shadow(0 6px 14px rgba(0,0,0,0.95))',
            letterSpacing: '-0.01em',
          }}>
            ETERNAL GREMLINS
          </h1>
          <div className="flex items-center justify-center gap-3 mt-3 mb-4">
            <div className="h-px w-24" style={{
              background: 'linear-gradient(90deg, transparent, rgba(200,65,60,0.85), transparent)',
              boxShadow: '0 0 8px rgba(200,65,60,0.6)',
            }} />
            <div className="w-1.5 h-1.5 rotate-45" style={{ background: '#c8413c', boxShadow: '0 0 10px #c8413c' }} />
            <div className="h-px w-24" style={{
              background: 'linear-gradient(90deg, transparent, rgba(200,65,60,0.85), transparent)',
              boxShadow: '0 0 8px rgba(200,65,60,0.6)',
            }} />
          </div>
          <p className="text-sm sm:text-base max-w-xl mx-auto leading-relaxed" style={{
            color: '#bba377', fontFamily: "'Cinzel', serif",
          }}>
            The four highest souls of every season — bound on chain by the Sovereign,
            placed forever among the altars of the dead.
          </p>
        </motion.div>

        {/* Filter pills */}
        <div className="flex flex-wrap justify-center gap-2 mt-8 mb-12" data-testid="season-filter">
          <FilterPill active={filter === 'all'} onClick={() => setFilter('all')} testId="season-filter-all">ALL SEASONS</FilterPill>
          {SEASONS.map((s) => (
            <FilterPill key={s} active={filter === s} onClick={() => setFilter(s)} testId={`season-filter-${s}`}>SEASON {s}</FilterPill>
          ))}
        </div>

        {/* PLINTH GRID — staggered necropolis */}
        {filteredGhosts.length === 0 ? (
          <div
            className="text-center py-24"
            data-testid="ghost-hall-empty"
            style={{
              border: '1px dashed rgba(140,100,40,0.3)',
              background: 'rgba(8,4,2,0.4)',
            }}
          >
            <Skull className="w-14 h-14 mx-auto mb-4" style={{ color: isLive ? 'rgba(125,208,255,0.35)' : 'rgba(220,170,80,0.3)', filter: 'drop-shadow(0 0 12px rgba(125,208,255,0.25))' }} />
            <p className="text-lg font-bold mb-2" style={{
              fontFamily: "'Cinzel', serif",
              color: isLive ? 'rgba(168,222,247,0.9)' : 'rgba(220,170,80,0.7)',
              letterSpacing: '0.04em',
            }}>
              {isLive ? 'No eternal gremlins yet' : 'The hall awaits its first ghost'}
            </p>
            <p
              className="text-[12.5px] mt-2 max-w-md mx-auto leading-relaxed"
              style={{
                fontFamily: "'EB Garamond', serif",
                fontStyle: 'italic',
                color: isLive ? 'rgba(168,222,247,0.55)' : 'rgba(220,200,160,0.55)',
              }}
            >
              {isLive
                ? 'Devnet is fresh. Eternal gremlins are minted on-chain via `immortalize_ghost` after a player tops the season leaderboard. Be the first to be remembered.'
                : 'Once the season ends, the highest-ranking gremlins are immortalised here forever.'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-14 sm:gap-y-16 items-end" data-testid="plinth-grid">
            {filteredGhosts.map((g) => (
              <Plinth key={`${g.season}-${g.rank}`} ghost={g} onSelect={setSelected} />
            ))}
          </div>
        )}

        {/* Footer engraving */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 1 }}
          className="mt-24 text-center max-w-xl mx-auto"
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,65,60,0.6), transparent)' }} />
            <span className="text-[10px] tracking-[0.3em]" style={{ color: '#9a7a3a', fontFamily: "'Cinzel', serif" }}>
              ◆ DEATH IS THE ONLY PROMOTION ◆
            </span>
            <div className="flex-1 h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(200,65,60,0.6), transparent)' }} />
          </div>
        </motion.div>
      </div>

      {/* Modal */}
      <AnimatePresence>
        {selected && <GhostPreviewModal ghost={selected} onClose={() => setSelected(null)} />}
      </AnimatePresence>
    </div>
  );
}
